/**
 * Materialize working UI state from a Mongo deal document.
 * Used by Open File and by boot rehydrate so every data point
 * (inputs, assumptions, scenarios, notes, cadastre, market, FX, cash)
 * comes from the server when a saved file is restored — not only this browser.
 */
import { validateInputs } from '../types/dealTypes';
import { buildMarketQueryKey } from './marketData';
import {
  hydrateScenarios,
  getActiveScenario,
  syncActiveScenario,
  resolveVersionAssumptionsOnOpen,
} from './dealScenarios';
import { hydrateCashTracker } from './cashTracker';
import { hydrateLandAssessmentStore } from './landAssessment';
import { hydrateStageFinanceAssumptionsStore } from './stageFinanceAssumptions';
import { buildDealPersistPayload } from './serverAutosave';
import { resolveCashTrackerFromServer } from './cashTrackerPersist';

/** Migrate v3→v4 (remove transferTaxRate). */
export function migrateDealState(state) {
  if (state?.assumptions?.transferTaxRate !== undefined) {
    const a = { ...state.assumptions };
    delete a.transferTaxRate;
    return { ...state, assumptions: a };
  }
  return state;
}

function withMarketQueryKey(market, address) {
  if (
    !market
    || market.status !== 'ok'
    || (market.data_type !== 'ai_estimate' && market.source !== 'AI Estimate')
    || market.query_key
  ) {
    return market || null;
  }
  const addr = address || '';
  if (!addr || String(addr).trim().length < 5) return market;
  return { ...market, query_key: buildMarketQueryKey(addr) };
}

/**
 * Build the same coherent state Open File applies, from a full deal JSON.
 */
export function materializeOpenedDeal(deal) {
  if (!deal || typeof deal !== 'object') return null;

  const migrated = migrateDealState({
    inputs: deal.inputs || {},
    assumptions: deal.assumptions || {},
  });
  const hydrated = hydrateScenarios({
    inputs: { ...migrated.inputs, comment: '' },
    assumptions: { ...migrated.assumptions, reportingCurrency: 'CHF' },
    scenarios: deal.scenarios,
    activeScenarioId: deal.activeScenarioId,
  });
  const active = getActiveScenario(hydrated.scenarios, hydrated.activeScenarioId);
  const nextInputs = validateInputs({
    ...(active?.inputs || migrated.inputs),
    comment: '',
  });
  const nextAssumptions = resolveVersionAssumptionsOnOpen(
    active?.assumptions || migrated.assumptions,
    deal.fxSnapshot,
  );
  const nextSnap = nextAssumptions.fxSnapshot || deal.fxSnapshot || null;
  const nextScenarios = syncActiveScenario(
    hydrated.scenarios,
    hydrated.activeScenarioId,
    nextInputs,
    nextAssumptions,
  );
  const nextNotes = deal.notes || '';
  const nextCadastre = deal.cadastreData || null;
  const nextMarket = withMarketQueryKey(
    deal.marketData || null,
    nextInputs.address || migrated.inputs?.address || '',
  );
  const nextCash = hydrateCashTracker(deal.cashTracker);
  const nextLand = hydrateLandAssessmentStore(deal.landAssessment);
  const nextStageFinance = hydrateStageFinanceAssumptionsStore(deal.stageFinanceAssumptions);

  return {
    id: deal.id,
    name: deal.name || null,
    inputs: nextInputs,
    assumptions: nextAssumptions,
    fxSnapshot: nextSnap,
    scenarios: nextScenarios,
    activeScenarioId: hydrated.activeScenarioId,
    notes: nextNotes,
    cadastreData: nextCadastre,
    marketData: nextMarket,
    cashTracker: nextCash,
    landAssessment: nextLand,
    stageFinanceAssumptions: nextStageFinance,
    persistPayload: buildDealPersistPayload(
      nextInputs,
      nextAssumptions,
      nextNotes,
      nextCadastre,
      nextMarket,
      nextSnap,
      nextScenarios,
      hydrated.activeScenarioId,
      nextCash,
      nextLand,
      nextStageFinance,
    ),
  };
}

/**
 * Prefer the server deal for all fields when boot restores a saved file.
 * Keep a local-only Cash Tracker launch when the server has none yet (upload path).
 */
export function resolveBootDealFromServer(serverDeal, localAutosave) {
  const materialized = materializeOpenedDeal(serverDeal);
  if (!materialized) {
    return { state: null, source: 'none', shouldUploadCash: false };
  }

  const cashResolved = resolveCashTrackerFromServer(
    serverDeal?.cashTracker,
    localAutosave?.cashTracker,
    hydrateCashTracker,
  );

  if (cashResolved.source === 'local' && cashResolved.shouldUpload) {
    return {
      state: {
        ...materialized,
        cashTracker: cashResolved.cashTracker,
        persistPayload: {
          ...materialized.persistPayload,
          cashTracker: cashResolved.cashTracker,
        },
      },
      source: 'server-with-local-cash',
      shouldUploadCash: true,
    };
  }

  return {
    state: materialized,
    source: 'server',
    shouldUploadCash: false,
  };
}
